import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { TZ_CHOICES, tzOffsetMinutes } from "../lib/format";
import { Card, CopyButton, Spinner } from "../components/ui";
import type { SiteOutletContext } from "./SiteLayout";

export default function SettingsPage() {
  const { site } = useOutletContext<SiteOutletContext>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [name, setName] = useState(site.name);
  const [domain, setDomain] = useState(site.domain ?? "");
  const [tz, setTz] = useState(site.tz);
  const [saved, setSaved] = useState(false);

  const [shareUrl, setShareUrl] = useState<string | null>(
    site.share_token ? `${location.origin}/share/${site.share_token}` : null,
  );

  const origin = location.origin;
  const snippet = `<script defer src="${origin}/script.js" data-website-id="${site.id}"></script>`;

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/api/sites/${site.id}`, { name, domain, tz, tz_offset: tzOffsetMinutes(tz) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["site", site.id] });
      queryClient.invalidateQueries({ queryKey: ["sites"] });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    },
  });

  const share = useMutation({
    mutationFn: (enabled: boolean) =>
      api.post<{ share_token: string | null }>(`/api/sites/${site.id}/share`, { enabled }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["site", site.id] });
      setShareUrl(res.share_token ? `${location.origin}/share/${res.share_token}` : null);
    },
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/api/sites/${site.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sites"] });
      queryClient.removeQueries({ queryKey: ["site", site.id] });
      navigate("/", { replace: true });
    },
  });

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card title="Detail situs">
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate();
          }}
        >
          <div>
            <label className="label" htmlFor="set-name">
              Nama
            </label>
            <input id="set-name" className="input" value={name} onChange={(event) => setName(event.target.value)} required />
          </div>
          <div>
            <label className="label" htmlFor="set-domain">
              Domain (pisahkan koma untuk beberapa)
            </label>
            <input id="set-domain" className="input" value={domain} onChange={(event) => setDomain(event.target.value)} />
          </div>
          <div>
            <label className="label" htmlFor="set-tz">
              Zona waktu
            </label>
            <select id="set-tz" className="input" value={tz} onChange={(event) => setTz(event.target.value)}>
              {TZ_CHOICES.map((zone) => (
                <option key={zone} value={zone}>
                  {zone}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center justify-end gap-2">
            {saved && <span className="text-xs text-emerald-600">Tersimpan</span>}
            <button type="submit" className="btn-primary" disabled={save.isPending}>
              {save.isPending && <Spinner className="text-white" />}
              Simpan
            </button>
          </div>
        </form>
      </Card>

      <div className="space-y-4">
        <Card title="Kode pelacak" actions={<CopyButton text={snippet} />}>
          <p className="mb-2 text-xs text-slate-500">
            Tempel sebelum tag <code>&lt;/head&gt;</code> pada situs Anda.
          </p>
          <pre className="overflow-x-auto rounded-lg bg-slate-900 p-3 text-xs text-slate-100">{snippet}</pre>
          <p className="mt-2 text-xs text-slate-500">
            Peristiwa kustom: <code>{'window.statistik.track("nama", { kunci: "nilai" })'}</code>
          </p>
        </Card>

        <Card
          title="Tautan publik"
          actions={
            <button
              type="button"
              className="btn text-xs"
              onClick={() => share.mutate(!site.share_token)}
              disabled={share.isPending}
            >
              {site.share_token ? "Nonaktifkan" : "Aktifkan"}
            </button>
          }
        >
          {shareUrl ? (
            <div className="flex items-center gap-2">
              <input readOnly className="input text-xs" value={shareUrl} />
              <CopyButton text={shareUrl} />
            </div>
          ) : (
            <p className="text-sm text-slate-500">Bagikan statistik ke publik tanpa login.</p>
          )}
        </Card>

        <Card title="Zona berbahaya">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-slate-500">
              Hapus situs beserta seluruh data statistiknya. Tindakan ini permanen.
            </p>
            <button
              type="button"
              className="btn-danger"
              disabled={remove.isPending}
              onClick={() => {
                if (window.confirm(`Hapus situs "${site.name}" beserta semua datanya?`)) remove.mutate();
              }}
            >
              Hapus situs
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
}
